<template>
  <div class="modal-overlay">
    <transition name="modal">
      <div class="modal-container" @click.stop>

        <!-- Header -->
        <div class="modal-header">
          <ion-icon name="create-outline" class="modal-icon" />
          <div class="modal-title">Editar Usuario</div>
          <div class="header-divider"></div>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">

            <!-- Nombre + Rol en fila -->
            <div class="row-fields">
              <div class="form-group">
                <label for="username">Nombre</label>
                <input
                  id="username"
                  type="text"
                  v-model="form.username"
                  placeholder="Nombre del usuario"
                  :class="{ error: errors.username }"
                />
                <span v-if="errors.username" class="error-msg">{{ errors.username }}</span>
              </div>

              <div class="form-group">
                <label for="role">Rol</label>
                <select
                  id="role"
                  v-model="form.role"
                  :class="{ error: errors.role }"
                >
                  <option value="" disabled>Selecciona un rol</option>
                  <option value="admin">Admin</option>
                  <option value="agent">Agente</option>
                </select>
                <span v-if="errors.role" class="error-msg">{{ errors.role }}</span>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <template v-if="!loading && !successMessage">
                <button type="button" class="cancel-btn" @click="closeModal">
                  Cancelar
                </button>
                <button type="submit" class="save-btn">
                  Actualizar
                </button>
              </template>

              <template v-else-if="loading">
                <div class="loading-msg">
                  <span class="spinner"></span>
                  Guardando cambios...
                </div>
              </template>

              <template v-else>
                <div class="success-msg">Usuario actualizado con éxito</div>
              </template>
            </div>

          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "EditUserModal",

  // ========== PROPIEDADES ==========
  props: {
    user: {
      type: Object,
      required: true,
    },
  },

  // ========== ESTADO LOCAL ==========
  data() {
    return {
      // Formulario con datos iniciales
      form: {
        username: this.user.username,
        role: this.user.role,
      },

      // Errores de validación
      errors: {},

      // Indicador de carga
      loading: false,

      // Mensaje de éxito
      successMessage: "",
    };
  },

  // ========== VALIDACIONES EN TIEMPO REAL ==========
  watch: {
    // Validación en vivo: Nombre
    "form.username"(value) {
      if (!value) {
        this.errors.username = "El nombre de usuario es obligatorio.";
      } else if (value.length < 3) {
        this.errors.username = "Debe tener al menos 3 caracteres.";
      } else {
        delete this.errors.username;
      }
    },

    // Validación en vivo: Rol
    "form.role"(value) {
      if (!value) {
        this.errors.role = "Selecciona un rol.";
      } else {
        delete this.errors.role;
      }
    },
  },

  // ========== MÉTODOS ==========
  methods: {
    // Cerrar modal
    closeModal() {
      this.$emit("close");
    },

    // Validar todos los campos manualmente
    validateAll() {
      if (!this.form.username) {
        this.errors.username = "El nombre de usuario es obligatorio.";
      }

      if (!this.form.role) {
        this.errors.role = "Selecciona un rol.";
      }

      return Object.keys(this.errors).length === 0;
    },

    // Enviar formulario
    async handleSubmit() {
      if (!this.validateAll()) return;

      this.loading = true;
      this.successMessage = "";

      try {
        const response = await this.$axios.put(`/users/${this.user.id}`, this.form);

        if (response.data?.user) {
          this.successMessage = "Usuario actualizado con éxito";

          // Emitir actualización y cerrar modal después de 1.2s
          setTimeout(() => {
            this.$emit("user-updated", response.data.user);
            this.closeModal();
          }, 1200);
        }
      } catch (error) {
        if (error.response?.data?.error) {
          this.errors = error.response.data.error;
        } else {
          console.error("Error al actualizar usuario:", error);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========== Overlay ========== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Manrope', sans-serif;
}

/* ========== Contenedor ========== */
.modal-container {
  background: #ffffff;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.2s ease-in-out;
}

/* ========== Header ========== */
.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.modal-icon {
  font-size: 1.4rem;
  color: #3b82f6;
}

.modal-header h2 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.header-divider {
  flex: 1;
  height: 1.3px;
  background: #e2e8f0;
}

/* ========== Formulario ========== */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1.25rem;
}

.form-group label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
}

.form-group input,
.form-group select {
  padding: 0.6rem 0.9rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 0.95rem;
  color: #0f172a;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  outline: none;
}

.form-group input.error,
.form-group select.error {
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3);
}

.form-group input::placeholder {
  color: #94a3b8;
}

/* ========== Errores ========== */
.error-msg {
  font-size: 0.8rem;
  color: #dc2626;
  font-weight: 500;
}

/* ========== Footer ========== */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 0.75rem;
}

.cancel-btn {
  padding: 0.55rem 1rem;
  background-color: #f8fafc;
  color: #334155;
  border: 1.5px solid #cbd5e1;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background-color: #e2e8f0;
  border-color: #94a3b8;
  color: #1e293b;
}

.save-btn {
  padding: 0.55rem 1.3rem;
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  transition: all 0.25s ease;
}

.save-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(37, 99, 235, 0.2);
}

.loading-msg,
.success-msg {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  background-color: #f1f5f9;
  padding: 0.55rem 1.3rem;
  border-radius: 0.65rem;
  border: 1px solid #cbd5e1;
  text-align: center;
}

.success-msg {
  background-color: #ecfdf5;
  border-color: #34d399;
  color: #059669;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
  margin-right: 0.6rem;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== Animación ========== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>